// EL ENLACE QUE TRAE LA TERMINAL DE PRODUCTO · DIRECTRIZ-TERMINAL-PRODUCTO
// F1, DISEÑO-F1 D18 y contrato v2 §9 (10 oct 2026).
//
// La terminal enseña dos QR a lo largo de su vida, y los dos llevan una
// URL que la cámara del teléfono abre en el navegador:
//   · al vincularse:  `<base>/T/<código de usuario>`         (§2.1)
//   · en cada turno:  `<base>/T/<código de terminal>/<nonce>` (§3.1)
// y hay un tercer camino sin código, `/t`, para «Agregar con código».
//
// NINGUNO ES UNA RUTA DE SERVIDOR. nginx devuelve la consola para
// cualquier camino (`frontend/nginx.conf`), y aquí se traduce la
// dirección a `?m=<módulo>` con su contexto, EXACTAMENTE como hace
// `accesos/enlaceDeEtiqueta.js` con `/q/`. Sin sesión, la dirección
// espera a que la persona entre: `AppShell` la lee al montarse.
//
// MAYÚSCULAS A PROPÓSITO. El QR va en modo alfanumérico (solo `0-9 A-Z`
// y unos pocos signos), así que la ruta que llega es `/T/K7P4QX`, no
// `/t/k7p4qx`. Se acepta en cualquier caja, y el código se normaliza
// con la misma regla que el campo de la pantalla.
import { CLAVE_MODULO } from '../lib/urlEstado';
import { reescribirUrlEn } from '../ui/navegacion';
import { normalizarCodigo, LARGO_CODIGO, LARGO_NONCE } from './codigoDeTerminal';

export const MODULO_TERMINALES = 'terminales';
export const MODULO_TURNO = 'terminal-turno';
export const MODULO_PASAR_LISTA = 'pasar-lista';

// Las claves que viajan en la dirección junto a `m`.
export const CLAVE_VINCULAR = 'vincular';
export const CLAVE_AGREGAR = 'agregar';
export const CLAVE_CODIGO = 'codigo';
export const CLAVE_NONCE = 'n';
// F1b: lo que la persona tecleó en `/t/<algo>` sin que llegue a ser un código.
export const CLAVE_ESCRITO = 'escrito';
export const CLAVE_K = 'k';
export const CLAVE_NUMERO = 'numero';

// 2.8.0 (DISEÑO-ASISTENCIA D9): el segundo segmento de `/T/{código}/{X}`
// se distingue por su largo CRUDO, ANTES de normalizar —normalizar recorta
// al largo pedido y una K de 10 caería en TURNO con un nonce truncado—:
// 4 dígitos = número (F-13), 10 de base32 = K del QR de asistencia, y lo
// demás sigue siendo el nonce de turno de 8.
const ES_NUMERO = /^[0-9]{4}$/;
const ES_K = /^[A-Za-z2-7]{10}$/;

const ES_DE_TERMINAL = /^\/t(?=\/|$)/i;

function segmento(texto, largo) {
  const { codigo } = normalizarCodigo(texto, largo);
  return codigo.length === largo ? codigo : null;
}

/**
 * Lo que dice un camino de la terminal, o null si no es uno:
 *   `/t`                 → { tipo: 'AGREGAR' }
 *   `/t/K7P4QX`          → { tipo: 'VINCULAR', codigo }
 *   `/t/M4R7WX/7H2M9QA3` → { tipo: 'TURNO', codigo, nonce }
 * `/tx`, `/t/a/b/c` y un código que no tiene la forma del contrato no
 * se tocan: la consola abre donde abriría de todos modos.
 */
export function leerEnlaceDeTerminal(camino, busqueda = '') {
  if (typeof camino !== 'string' || !ES_DE_TERMINAL.test(camino)) return null;
  let resto = camino.slice(2).replace(/^\/+/, '').replace(/\/+$/, '');
  try { resto = decodeURIComponent(resto); } catch { /* se queda tal cual */ }
  resto = resto.trim();
  if (!resto) return { tipo: 'AGREGAR' };
  // 2.11.0 · H1 (DISEÑO-ACTIVACION-EN-LINEA §6.3): `/t/vincular?c=EET5D6` es
  // el QR de la terminal que se vincula en línea. «vincular» es palabra
  // RESERVADA y se mira ANTES de normalizar: normalizada, «VINCULAR» pierde
  // la I y la L y queda «VNCUAR», seis caracteres válidos, y abría el
  // asistente buscando el código VNC-UAR. El código va en `c`.
  if (/^vincular$/i.test(resto)) {
    let c = '';
    try { c = new URLSearchParams(busqueda || '').get('c') || ''; } catch { c = ''; }
    const codigo = segmento(c, LARGO_CODIGO);
    if (codigo) return { tipo: 'VINCULAR', codigo };
    const escrito = c.replace(/[^0-9A-Za-z -]/g, '').slice(0, 12);
    return escrito ? { tipo: 'AGREGAR', escrito } : { tipo: 'AGREGAR' };
  }
  const partes = resto.split('/');
  if (partes.length === 1) {
    const codigo = segmento(partes[0], LARGO_CODIGO);
    if (codigo) return { tipo: 'VINCULAR', codigo };
    // ESCRITO A MANO Y A MEDIAS (F1b, pedido del dueño con la placa real):
    // `tegocloud.mx/t/k7p4q` abre «Agregar terminal» con lo tecleado en el
    // campo, que dice qué falta o qué carácter no entra. Antes caía en la
    // consola sin decir nada. Con espacios (`k7p 4qx`) llega como `%20` y ya
    // se normaliza arriba.
    const escrito = partes[0].replace(/[^0-9A-Za-z -]/g, '').slice(0, 12);
    return escrito ? { tipo: 'AGREGAR', escrito } : null;
  }
  if (partes.length === 2) {
    const crudo = partes[1].trim();
    if (ES_NUMERO.test(crudo) || ES_K.test(crudo)) {
      const codigo = segmento(partes[0], LARGO_CODIGO);
      if (!codigo) return null;
      return ES_NUMERO.test(crudo)
        ? { tipo: 'PASAR_LISTA', codigo, numero: crudo }
        : { tipo: 'PASAR_LISTA', codigo, k: crudo.toUpperCase() };
    }
    const codigo = segmento(partes[0], LARGO_CODIGO);
    const nonce = segmento(partes[1], LARGO_NONCE);
    return codigo && nonce ? { tipo: 'TURNO', codigo, nonce } : null;
  }
  return null;
}

/** La consulta (`?m=…`) a la que lleva lo leído. */
export function consultaDe(leido) {
  const q = new URLSearchParams();
  if (leido.tipo === 'PASAR_LISTA') {
    q.set(CLAVE_MODULO, MODULO_PASAR_LISTA);
    q.set(CLAVE_CODIGO, leido.codigo);
    if (leido.k) q.set(CLAVE_K, leido.k);
    if (leido.numero) q.set(CLAVE_NUMERO, leido.numero);
  } else if (leido.tipo === 'TURNO') {
    q.set(CLAVE_MODULO, MODULO_TURNO);
    q.set(CLAVE_CODIGO, leido.codigo);
    q.set(CLAVE_NONCE, leido.nonce);
  } else if (leido.tipo === 'VINCULAR') {
    q.set(CLAVE_MODULO, MODULO_TERMINALES);
    q.set(CLAVE_VINCULAR, leido.codigo);
  } else {
    q.set(CLAVE_MODULO, MODULO_TERMINALES);
    q.set(CLAVE_AGREGAR, '1');
    if (leido.escrito) q.set(CLAVE_ESCRITO, leido.escrito);
  }
  return q.toString();
}

/**
 * Si la dirección es un camino de la terminal, la cambia (sin dejar
 * rastro en el historial) por `/app?m=…` con su contexto. Devuelve lo que
 * leyó, o null si no era de la terminal.
 */
export function llevarEnlaceATerminales(win = window) {
  const leido = leerEnlaceDeTerminal(win.location.pathname, win.location.search);
  if (!leido) return null;
  try {
    // Sin borrar el state de la entrada (TC-DR-AT), igual que `/q/`.
    reescribirUrlEn(win, `/app?${consultaDe(leido)}`);
  } catch { /* sin historial, se queda donde estaba */ }
  return leido;
}

/**
 * El contexto que la pantalla lee de la dirección cuando el módulo de
 * navegación no se lo entrega. Es el respaldo de `CONTEXTO_EN_URL`
 * (`ui/navegacionContexto.js`, del integrador), que declara qué claves
 * viajan por módulo: mientras esa tabla no conozca `terminales` ni
 * `terminal-turno`, la pantalla igual sabe a qué vino.
 */
export function contextoDeTerminal(modulo, busqueda) {
  let q;
  try { q = new URLSearchParams(busqueda ?? ''); } catch { return null; }
  if (q.get(CLAVE_MODULO) !== modulo) return null;
  const ctx = {};
  if (modulo === MODULO_TURNO) {
    const codigo = segmento(q.get(CLAVE_CODIGO) || '', LARGO_CODIGO);
    const nonce = segmento(q.get(CLAVE_NONCE) || '', LARGO_NONCE);
    if (codigo) ctx.codigo = codigo;
    if (nonce) ctx.n = nonce;
  } else if (modulo === MODULO_PASAR_LISTA) {
    const codigo = segmento(q.get(CLAVE_CODIGO) || '', LARGO_CODIGO);
    const k = (q.get(CLAVE_K) || '').trim();
    const numero = (q.get(CLAVE_NUMERO) || '').trim();
    if (codigo) ctx.codigo = codigo;
    if (ES_K.test(k)) ctx.k = k.toUpperCase();
    if (ES_NUMERO.test(numero)) ctx.numero = numero;
  } else if (modulo === MODULO_TERMINALES) {
    const vincular = segmento(q.get(CLAVE_VINCULAR) || '', LARGO_CODIGO);
    if (vincular) ctx.vincular = vincular;
    if (q.get(CLAVE_AGREGAR)) ctx.agregar = q.get(CLAVE_AGREGAR);
    if (q.get(CLAVE_ESCRITO)) ctx.escrito = q.get(CLAVE_ESCRITO).slice(0, 12);
  }
  return Object.keys(ctx).length ? ctx : null;
}
